import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { InfoCircle } from '@untitled-ui/icons-react';
import { useState } from 'react';

import { AddBrandDialog } from '../../../components/add-brand-dialog';
import { Button } from '../../../components/buttons/button';
import { FeaturedIcon } from '../../../components/featured-icon';
import { ToastVariant } from '../../../components/toast';
import { ForbiddenError } from '../../../error';
import { convertError } from '../../../error/convert';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { getQueryClient } from '../../../sdk/request';
import { getAuthUserFromCache, useUpdateUser } from '../../../services/storage';
import { useCreateBrand } from '../../../services/storage/brands-queries';
import { tNamespace } from '../../../services/translation';
import { createBrandCreateData, setSelectedBrand } from '../../../shared/brand';
import { useToast } from '../../../shared/contexts/toast-context';
import { getSelectedOrganization } from '../../../shared/organization';

const t = tNamespace('app.screen.no-brand');
const tErrors = tNamespace('errors');

export const Route = createFileRoute('/app/_screen/no-brand')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const [open, setOpen] = useState(false);
  const addToast = useToast();
  const navigate = useNavigate();
  // TanStack Query hooks
  const queryClient = getQueryClient();
  const currentUser = getAuthUserFromCache(queryClient);
  const updateUserMutation = useUpdateUser();
  const createBrandMutation = useCreateBrand();
  const organization = getSelectedOrganization(currentUser);

  function handleAddBrand(data: { name: string; websiteUrl: string }) {
    if (organization == null) {
      return;
    }

    const { name, websiteUrl } = data;
    const brandData = createBrandCreateData(organization._id, name, websiteUrl);

    createBrandMutation.mutate(
      { data: brandData, isFromOnboarding: false },
      {
        onSuccess: function (createdBrand) {
          addToast({
            id: Date.now(),
            title: t('brandCreated'),
            variant: ToastVariant.Success,
          });
          if (createdBrand == null) {
            return;
          }

          setSelectedBrand(createdBrand._id).catch(function (err) {
            captureException(
              err,
              {
                file: 'app/_screen/no-brand.tsx',
                method: 'handleAddBrand',
                infoMessage: 'unable to set selected brand',
              },
              trackError
            );
          });

          updateUserMutation.mutate(
            { selectedBrandId: createdBrand._id },
            {
              onSuccess: function () {
                void navigate({ to: '/app' });
              },
              onError: function (err) {
                captureException(
                  err,
                  {
                    file: 'app/_screen/no-brand.tsx',
                    method: 'handleAddBrand',
                    infoMessage: 'unable to set selected brand after creating',
                  },
                  trackError
                );
              },
            }
          );
        },
        onError: function (err) {
          const handledErr = convertError(err);

          // handle forbiddens (which comes from plan features limitations)
          // as such, the original message is a specific enum incoming
          if (handledErr instanceof ForbiddenError) {
            addToast({
              variant: ToastVariant.Error,
              title: tErrors(handledErr.originalMessage),
              id: Date.now(),
            });
            return;
          }

          addToast({
            id: Date.now(),
            title: t('cannotCreateBrand'),
            variant: ToastVariant.Error,
          });
          captureException(
            err,
            {
              file: 'app/_screen/no-brand.tsx',
              method: 'handleAddBrand',
              infoMessage: 'unable to create brand',
            },
            trackError
          );
        },
      }
    );
  }
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="flex flex-col items-center justify-center">
        <FeaturedIcon icon={<InfoCircle />} color="gray" />
        <p className="font-display-3 mb-4 mt-8">{t('brandFailed')}</p>
        <p className="font-body-2-r max-w-[450px] text-center text-branding-gray">
          {t('brandFailedDescription')}
        </p>
        <Button
          className="mt-8"
          onClick={function () {
            setOpen(true);
          }}
        >
          {t('setupFirstBrand')}
        </Button>
      </div>
      <AddBrandDialog
        open={open}
        onOpenChange={function (open) {
          setOpen(open);
        }}
        onAddBrand={handleAddBrand}
      />
    </div>
  );
}
